import * as React from 'react'
import { format } from 'date-fns'
import { pl, enUS } from 'date-fns/locale'
import { FileRecord, Folder } from '@taskdashboard/types'
import {
  Folder as FolderIcon,
  MoreHorizontal,
  FileText,
  Image,
  FileSpreadsheet,
  Video,
  Music,
  File as GenericFile,
  Pencil,
  Trash2,
  Copy,
  Archive,
  Info,
  FolderOpen,
  Download,
  ArrowUp,
  ArrowDown,
  Eye,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useTranslation } from 'react-i18next'

interface FileListProps {
  files?: FileRecord[]
  folders?: Folder[]
  isLoading?: boolean
  onNavigate: (id: string) => void
  onRename: (id: string) => void
  onDelete: (id: string) => void
  onMove: (id: string) => void
  onDownload: (id: string) => void
  onInfo?: (id: string) => void
  onArchive?: (id: string) => void
  onDuplicate?: (id: string) => void
  onOpen?: (id: string) => void
  sortBy: 'name' | 'size' | 'date' | 'type'
  sortOrder: 'asc' | 'desc'
  onSort: (field: 'name' | 'size' | 'date' | 'type') => void
  userRole?: string | null
}

// Get appropriate icon based on file type
function getFileIcon(mimeType?: string | null) {
  if (!mimeType) return GenericFile
  if (mimeType.startsWith('image/')) return Image
  if (mimeType.startsWith('video/')) return Video
  if (mimeType.startsWith('audio/')) return Music
  if (mimeType.includes('pdf')) return FileText
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return FileSpreadsheet
  if (mimeType.includes('document') || mimeType.includes('word')) return FileText
  return GenericFile
}

// Get color based on file type
function getFileColor(mimeType?: string | null) {
  if (!mimeType) return 'text-gray-500'
  if (mimeType.startsWith('image/')) return 'text-purple-400'
  if (mimeType.startsWith('video/')) return 'text-pink-400'
  if (mimeType.startsWith('audio/')) return 'text-green-400'
  if (mimeType.includes('pdf')) return 'text-red-400'
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return 'text-emerald-400'
  if (mimeType.includes('document') || mimeType.includes('word')) return 'text-blue-400'
  return 'text-gray-500'
}

export function FileList({
  files = [],
  folders = [],
  isLoading,
  onNavigate,
  onRename,
  onDelete,
  onMove,
  onDownload,
  onInfo,
  onArchive,
  onDuplicate,
  onOpen,
  sortBy,
  sortOrder,
  onSort,
  userRole,
}: FileListProps) {
  const { t, i18n } = useTranslation()
  const currentLocale = i18n.language === 'pl' ? pl : enUS

  // Get friendly file type name
  const getFriendlyFileType = (mimeType?: string | null): string => {
    if (!mimeType) return t('files.types.all') // Or Unknown?

    const mimeMap: Record<string, string> = {
      'application/pdf': t('files.types.pdf'),
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        t('files.types.document'),
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
        t('files.types.spreadsheet'),
      'application/vnd.openxmlformats-officedocument.presentationml.presentation':
        t('files.types.powerpoint'),
      'application/msword': t('files.types.document'),
      'application/vnd.ms-excel': t('files.types.spreadsheet'),
      'application/vnd.ms-powerpoint': t('files.types.powerpoint'),
      'application/zip': t('files.types.archive'),
      'application/json': t('files.types.json'),
      'text/plain': t('files.types.text'),
    }

    if (mimeMap[mimeType]) return mimeMap[mimeType]
    if (mimeType.startsWith('image/')) return t('files.types.image')
    if (mimeType.startsWith('video/')) return t('files.types.video')
    if (mimeType.startsWith('audio/')) return t('files.types.audio')

    return mimeType.split('/')[1]?.toUpperCase() || t('files.types.unknown')
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-10 text-gray-500">
        {t('files.messages.getting_ready')}
      </div>
    )
  }

  const formatSize = (bytes: number | null | undefined) => {
    if (bytes == null) return '-'
    if (bytes === 0) return '0 B'
    const units = ['B', 'KB', 'MB', 'GB']
    let size = bytes
    let unitIndex = 0
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024
      unitIndex++
    }
    return `${size.toFixed(1)} ${units[unitIndex]}`
  }

  const renderSortIcon = (field: 'name' | 'size' | 'date' | 'type') => {
    if (sortBy !== field) return null
    return sortOrder === 'asc' ? (
      <ArrowUp className="ml-1 h-4 w-4" />
    ) : (
      <ArrowDown className="ml-1 h-4 w-4" />
    )
  }

  const canManageFile = userRole !== 'member'
  const canManageFolder = userRole !== 'member'

  return (
    <div className="overflow-hidden rounded-xl border border-[var(--app-divider)] bg-[var(--app-bg-card)]">
      <table className="w-full">
        <thead>
          <tr className="border-b border-white/5">
            <th
              className="w-[50%] cursor-pointer px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-[var(--app-text-muted)] transition-colors hover:text-[var(--app-text-primary)]"
              onClick={() => onSort('name')}
            >
              <div className="flex items-center">
                {t('files.sort.name')} {renderSortIcon('name')}
              </div>
            </th>
            <th
              className="cursor-pointer px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-[var(--app-text-muted)] transition-colors hover:text-[var(--app-text-primary)]"
              onClick={() => onSort('size')}
            >
              <div className="flex items-center">
                {t('files.sort.size')} {renderSortIcon('size')}
              </div>
            </th>
            <th
              className="cursor-pointer px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-[var(--app-text-muted)] transition-colors hover:text-[var(--app-text-primary)]"
              onClick={() => onSort('type')}
            >
              <div className="flex items-center">
                {t('files.sort.type')} {renderSortIcon('type')}
              </div>
            </th>
            <th
              className="cursor-pointer px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-[var(--app-text-muted)] transition-colors hover:text-[var(--app-text-primary)]"
              onClick={() => onSort('date')}
            >
              <div className="flex items-center">
                {t('files.properties.modified')} {renderSortIcon('date')}
              </div>
            </th>
            <th className="w-[50px] px-4 py-3"></th>
          </tr>
        </thead>
        <tbody>
          {folders.map((folder) => (
            <tr
              key={folder.id}
              className="cursor-pointer border-b border-white/5 transition-colors hover:bg-[var(--app-bg-elevated)]"
              onClick={() => onNavigate(folder.id)}
            >
              <td className="px-4 py-3">
                <div className="flex items-center gap-3">
                  <div className="bg-[var(--app-accent)]/10 flex h-9 w-9 items-center justify-center rounded-lg">
                    <FolderIcon className="fill-[var(--app-accent)]/20 h-4 w-4 text-[var(--app-accent)]" />
                  </div>
                  <span className="text-sm font-medium text-[var(--app-text-primary)]">
                    {folder.name}
                  </span>
                </div>
              </td>
              <td className="px-4 py-3 text-sm text-[var(--app-text-muted)]">
                {formatSize((folder as any).size)}
              </td>
              <td className="px-4 py-3 text-sm text-[var(--app-text-muted)]">
                {t('files.types.folder')}
              </td>
              <td className="px-4 py-3 text-sm text-[var(--app-text-muted)]">
                {format(new Date(folder.updatedAt || folder.createdAt), 'dd MMM, yyyy', {
                  locale: currentLocale,
                })}
              </td>
              <td className="px-4 py-3" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className="h-8 w-8 p-0 text-[var(--app-text-muted)] hover:bg-[var(--app-bg-elevated)] hover:text-[var(--app-text-primary)]"
                    >
                      <span className="sr-only">Open menu</span>
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="w-40 border-[var(--app-divider)] bg-[var(--app-bg-card)] p-1"
                  >
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation()
                        onDownload(folder.id)
                      }}
                      className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-[var(--app-text-secondary)] hover:bg-[var(--app-bg-elevated)] hover:text-[var(--app-text-primary)]"
                    >
                      <Download className="h-4 w-4 text-[var(--app-text-muted)]" />
                      <span>{t('files.actions.download')}</span>
                    </DropdownMenuItem>
                    {canManageFolder && (
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation()
                          onRename(folder.id)
                        }}
                        className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-[var(--app-text-secondary)] hover:bg-[var(--app-bg-elevated)] hover:text-[var(--app-text-primary)]"
                      >
                        <Pencil className="h-4 w-4 text-[var(--app-accent)]" />
                        <span>{t('files.actions.edit')}</span>
                      </DropdownMenuItem>
                    )}
                    {canManageFolder && (
                      <>
                        <DropdownMenuItem
                          onClick={(e) => e.stopPropagation()}
                          className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-[var(--app-text-secondary)] hover:bg-[var(--app-bg-elevated)] hover:text-[var(--app-text-primary)]"
                        >
                          <Copy className="h-4 w-4 text-[var(--app-text-muted)]" />
                          <span>{t('files.actions.duplicate')}</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={(e) => e.stopPropagation()}
                          className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-[var(--app-text-secondary)] hover:bg-[var(--app-bg-elevated)] hover:text-[var(--app-text-primary)]"
                        >
                          <Archive className="h-4 w-4 text-[var(--app-text-muted)]" />
                          <span>{t('files.actions.archive')}</span>
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuItem
                      onClick={(e) => e.stopPropagation()}
                      className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-[var(--app-text-secondary)] hover:bg-[var(--app-bg-elevated)] hover:text-[var(--app-text-primary)]"
                    >
                      <Info className="h-4 w-4 text-[var(--app-text-muted)]" />
                      <span>{t('files.actions.info')}</span>
                    </DropdownMenuItem>
                    {canManageFolder && (
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation()
                          onDelete(folder.id)
                        }}
                        className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-amber-500 hover:bg-amber-500/10 hover:text-amber-400"
                      >
                        <Trash2 className="h-4 w-4 text-amber-500" />
                        <span>{t('files.actions.delete')}</span>
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </td>
            </tr>
          ))}

          {files.map((file) => {
            const FileTypeIcon = getFileIcon(file.mimeType)
            const iconColor = getFileColor(file.mimeType)

            return (
              <tr
                key={file.id}
                className="cursor-pointer border-b border-white/5 transition-colors hover:bg-[var(--app-bg-elevated)]"
                onClick={() => onInfo?.(file.id)}
                onDoubleClick={(e) => {
                  e.stopPropagation()
                  onOpen?.(file.id)
                }}
              >
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/5 bg-[var(--app-bg-elevated)]">
                      <FileTypeIcon className={`h-4 w-4 ${iconColor}`} />
                    </div>
                    <span className="text-sm font-medium text-[var(--app-text-primary)]">
                      {file.name}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-[var(--app-text-muted)]">
                  {formatSize(file.size)}
                </td>
                <td className="px-4 py-3 text-sm text-[var(--app-text-muted)]">
                  {getFriendlyFileType(file.mimeType)}
                </td>
                <td className="px-4 py-3 text-sm text-[var(--app-text-muted)]">
                  {format(new Date(file.updatedAt || file.createdAt), 'dd MMM, yyyy', {
                    locale: currentLocale,
                  })}
                </td>
                <td className="px-4 py-3">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        className="h-8 w-8 p-0 text-[var(--app-text-muted)] hover:bg-[var(--app-bg-elevated)] hover:text-[var(--app-text-primary)]"
                      >
                        <span className="sr-only">Open menu</span>
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="end"
                      className="w-40 border-[var(--app-divider)] bg-[var(--app-bg-card)] p-1"
                    >
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation()
                          onOpen?.(file.id)
                        }}
                        className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-[var(--app-text-secondary)] hover:bg-[var(--app-bg-elevated)] hover:text-[var(--app-text-primary)]"
                      >
                        <Eye className="h-4 w-4 text-[var(--app-text-muted)]" />
                        <span>{t('files.actions.open')}</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation()
                          onDownload(file.id)
                        }}
                        className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-[var(--app-text-secondary)] hover:bg-[var(--app-bg-elevated)] hover:text-[var(--app-text-primary)]"
                      >
                        <Download className="h-4 w-4 text-[var(--app-text-muted)]" />
                        <span>{t('files.actions.download')}</span>
                      </DropdownMenuItem>
                      <div className="my-1 h-px bg-[var(--app-divider)]"></div>
                      {canManageFile && (
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation()
                            onRename(file.id)
                          }}
                          className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-[var(--app-text-secondary)] hover:bg-[var(--app-bg-elevated)] hover:text-[var(--app-text-primary)]"
                        >
                          <Pencil className="h-4 w-4 text-[var(--app-accent)]" />
                          <span>{t('files.actions.rename')}</span>
                        </DropdownMenuItem>
                      )}
                      {onArchive && canManageFile && (
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation()
                            onArchive(file.id)
                          }}
                          className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-[var(--app-text-secondary)] hover:bg-[var(--app-bg-elevated)] hover:text-[var(--app-text-primary)]"
                        >
                          <Archive className="h-4 w-4 text-[var(--app-text-muted)]" />
                          <span>{t('files.actions.archive')}</span>
                        </DropdownMenuItem>
                      )}
                      {canManageFile && (
                        <>
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation()
                              onDuplicate?.(file.id)
                            }}
                            className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-[var(--app-text-secondary)] hover:bg-[var(--app-bg-elevated)] hover:text-[var(--app-text-primary)]"
                          >
                            <Copy className="h-4 w-4 text-[var(--app-text-muted)]" />
                            <span>{t('files.actions.duplicate')}</span>
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation()
                              onMove(file.id)
                            }}
                            className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-[var(--app-text-secondary)] hover:bg-[var(--app-bg-elevated)] hover:text-[var(--app-text-primary)]"
                          >
                            <FolderOpen className="h-4 w-4 text-[var(--app-text-muted)]" />
                            <span>{t('files.actions.move')}</span>
                          </DropdownMenuItem>
                        </>
                      )}
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation()
                          onInfo?.(file.id)
                        }}
                        className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-[var(--app-text-secondary)] hover:bg-[var(--app-bg-elevated)] hover:text-[var(--app-text-primary)]"
                      >
                        <Info className="h-4 w-4 text-[var(--app-text-muted)]" />
                        <span>{t('files.actions.info')}</span>
                      </DropdownMenuItem>
                      {canManageFile && (
                        <>
                          <div className="my-1 h-px bg-[var(--app-divider)]"></div>
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation()
                              onDelete(file.id)
                            }}
                            className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-amber-500 hover:bg-amber-500/10 hover:text-amber-400"
                          >
                            <Trash2 className="h-4 w-4" />
                            <span>{t('files.actions.delete')}</span>
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            )
          })}

          {folders.length === 0 && files.length === 0 && (
            <tr>
              <td colSpan={5} className="h-24 text-center text-[var(--app-text-muted)]">
                {t('files.picker.no_files')}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}
